import { useEffect, useState } from 'react';
import styles from './AmountAlert.module.scss';
import { Dialog, VisuallyHidden } from 'radix-ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useTheme from '../../../hooks/useTheme';
import Button from '../../atoms/Button/Button';
import { Transaction } from '../../../services/types';
import SupportButton from '../../atoms/SupportButton/SupportButton';
import { getCurrencySymbol } from '../../../services/utils';

interface Props {
  transaction: Transaction;
  /** The client refuses the adjusted amount: the deposit is cancelled. */
  onDecline: () => Promise<void> | void;
}

// Closed by its buttons only: a tap past the dialog or Escape must not read as agreement.
const keepOpen = (event: Event) => event.preventDefault();

const AmountAlert = ({ transaction, onDecline }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [declining, setDeclining] = useState<boolean>(false);
  // Знак валюты — от тикета: прибитый рубль подписывал бы чужой валютой любую сумму,
  // которую поменяли на депозите в другой валюте.
  const currency = getCurrencySymbol(
    transaction?.requisite?.currency || transaction?.currency,
  );
  const amount = `${transaction?.amount}${currency}`;

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = 'auto';
    }
  }, [isOpen]);

  const decline = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await onDecline();
    } finally {
      setDeclining(false);
    }
  };

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            styles.overlay,
            theme === 'dark' ? styles.dark : styles.light
          )}
        >
          <div className={styles.wrapper}>
            {transaction.merchant.toLowerCase() === 'vocxod' && (
              <div style={{ marginBottom: '28px' }}>
                <SupportButton userId={transaction?.externalUserId} />
              </div>
            )}
            <Dialog.Content
              className={styles.modal}
              onEscapeKeyDown={keepOpen}
              onPointerDownOutside={keepOpen}
              onInteractOutside={keepOpen}
            >
              <Dialog.Title className={styles.title}>{t('amountChange.title')}</Dialog.Title>
              <VisuallyHidden.Root>
                <Dialog.Description>{t('amountChange.reason')}</Dialog.Description>
              </VisuallyHidden.Root>
              <p className={styles.secondText}>{t('amountChange.reason')}</p>
              <div className={styles.amounts}>
                <span className={styles.sendLabel}>{t('amountChange.sendLabel')}</span>
                <h4>{amount}</h4>
                {transaction?.oldAmount != null && (
                  <p>
                    {t('amountChange.insteadOf', {
                      amount: `${transaction.oldAmount}${currency}`,
                    })}
                  </p>
                )}
              </div>
              <p className={styles.primaryText}>{t('amountChange.promise', { amount })}</p>
              <p className={styles.secondText}>{t('amountChange.exact')}</p>
              <div className={styles.actions}>
                <Button
                  onClick={() => setIsOpen(false)}
                  disabled={declining}
                  dataSentryElement='amount-alert-dismiss'
                >
                  {t('amountChange.continue')}
                </Button>
                <button
                  type='button'
                  className={styles.refuse}
                  disabled={declining}
                  onClick={decline}
                  data-sentry-element='amount-alert-refuse'
                >
                  {t('amountChange.refuse')}
                </button>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AmountAlert;
